import { useCallback, useEffect, useState } from 'react';
import { useWtrSelector } from 'redux/hooks';
import {
  getInterstitialsPageNumber,
  getInterstitialsSteps,
  isInterstitialLoading,
} from 'redux/modules/interstitials/selectors';
import { getPathname } from 'redux/modules/routing/selectors';
import { getFeatureFlags } from 'utils/feature-flags';
import {
  pushCompleteMissedOffersInterstitial,
  pushCompleteInterstitials,
  pushListInterstitials,
  pushProceedInterstitials,
  pushViewInterstitials,
  pushViewMissedOffersInterstitial,
} from 'utils/gtm/new';
import root from 'window-or-global';

export const useGAInterstitials = ({
  preventOverallEvents,
}: { preventOverallEvents?: boolean } = {}) => {
  const { EoS_interstitialsAnalytics: eosInterstitialsAnalytics } = getFeatureFlags();
  const [listReported, setListReported] = useState(false);
  const [reportedOffers, setReportedOffers] = useState<string[]>([]);
  const loading = useWtrSelector(isInterstitialLoading);
  const path = useWtrSelector(getPathname);
  const location = root?.location?.href;
  const interstitialsSteps = useWtrSelector(getInterstitialsSteps);
  const currentStep = useWtrSelector(getInterstitialsPageNumber);
  const interstitialsList = interstitialsSteps.map(({ label }: { label: string }) => label);
  const hasSteps = !loading && interstitialsSteps.length > 0;

  const { id, label } =
    currentStep && currentStep - 1 >= 0
      ? interstitialsSteps[currentStep - 1]
      : { id: undefined, label: undefined };

  useEffect(() => {
    if (eosInterstitialsAnalytics && hasSteps && !preventOverallEvents) {
      pushViewInterstitials({
        location,
        index: currentStep,
        interstitialId: id,
        interstitialTitle: label,
        path,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interstitialsSteps, loading]);

  const handleInterstitialClick = useCallback(
    ({ empty = false }: { empty?: boolean } = {}) => {
      if (eosInterstitialsAnalytics && hasSteps) {
        pushProceedInterstitials({
          location,
          index: currentStep,
          interstitialId: id,
          interstitialTitle: empty ? `${label} [empty]` : label,
          path,
        });
      }
    },
    [currentStep, eosInterstitialsAnalytics, hasSteps, id, label, location, path],
  );

  const handleCompleteInterstitials = useCallback(() => {
    const interstitialList = interstitialsSteps.map(
      ({ label: interstitialLabel }: { label: string }) => interstitialLabel,
    );
    if (eosInterstitialsAnalytics) {
      pushCompleteInterstitials({ interstitialList });
    }
  }, [eosInterstitialsAnalytics, interstitialsSteps]);

  const handleViewMissedOffer = useCallback(
    ({ offerDescription, offerId }: { offerDescription: string; offerId: string }) => {
      if (
        eosInterstitialsAnalytics &&
        hasSteps &&
        label === 'Missed offers' &&
        !reportedOffers.includes(offerId)
      ) {
        setReportedOffers([...reportedOffers, offerId]);
        pushViewMissedOffersInterstitial({
          offerDescription,
          offerId,
        });
      }
    },
    [eosInterstitialsAnalytics, hasSteps, label, reportedOffers],
  );

  const handleCompleteMissedOffer = useCallback(
    ({ offerDescription, offerId }: { offerDescription: string; offerId: string }) => {
      if (eosInterstitialsAnalytics && hasSteps) {
        pushCompleteMissedOffersInterstitial({
          offerDescription,
          offerId,
        });
      }
    },
    [eosInterstitialsAnalytics, hasSteps],
  );

  useEffect(() => {
    if (eosInterstitialsAnalytics && hasSteps && !listReported && !preventOverallEvents) {
      pushListInterstitials(interstitialsList);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setListReported(true);
    }
  }, [eosInterstitialsAnalytics, hasSteps, interstitialsList, listReported, preventOverallEvents]);

  return {
    handleCompleteInterstitials,
    handleInterstitialClick,
    handleViewMissedOffer,
    handleCompleteMissedOffer,
  };
};
